import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  input,
  output,
  signal
} from "@angular/core";
import { NgFor, NgIf } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatChipsModule } from "@angular/material/chips";

import { Profile } from "../../models/profile";

export type SwipeDecision = "left" | "right";

/**
 * One card in the swipe deck: drag to like/nope, tap photo edges to cycle images,
 * or use the action buttons. Only the top card (`isTop`) accepts drags and clicks.
 *
 * Transform and transition are applied on the host so the whole card moves; `stackIndex`
 * adds a slight scale/offset so stacked cards read visually behind the top one.
 *
 * Horizontal/vertical drag offset (`deckDx` / `deckDy`) applies only to the top card; cards behind it stay fixed in their stacked positions until they become the top card.
 */
@Component({
  selector: "app-swipe-card",
  standalone: true,
  imports: [NgIf, NgFor, MatCardModule, MatButtonModule, MatIconModule, MatChipsModule],
  templateUrl: "./swipe-card.component.html",
  styleUrl: "./swipe-card.component.scss",
  host: {
    // Draw order: top of stack (index 0) above the cards behind it.
    "[style.zIndex]": "1000 - stackIndex()",
    "[style.transform]": "stackTransform()",
    // While dragging, transition is disabled so the finger tracks 1:1; otherwise snap-back / fly-out animate.
    "[style.transition]": "hostTransition()",
    "[class.is-top]": "isTop()",
    "(pointerdown)": "onPointerDown($event)",
    "(lostpointercapture)": "onLostPointerCapture()",
    "(pointercancel)": "onLostPointerCapture()"
  }
})
export class SwipeCardComponent {
  private _profile!: Profile;
  /** When the bound profile’s id changes (including ngFor reuse), reset carousel and motion so state does not leak. */
  @Input({ required: true })
  set profile(p: Profile) {
    const idChanged = this._profile?.id !== p.id;
    this._profile = p;
    if (idChanged) {
      this.photoIndex.set(0);
      this.resetMotionState();
    }
  }
  get profile(): Profile {
    return this._profile;
  }

  /** 0 = front card; higher values are drawn smaller and slightly lower (stacked deck look). */
  readonly stackIndex = input(0);
  /** Signal inputs so `stackTransform` (a computed) invalidates when stack role changes after a swipe. */
  readonly isTop = input(false);

  /** Shared with sibling cards: same translate so the whole stack moves with the drag. */
  readonly deckDx = input(0);
  readonly deckDy = input(0);
  /** When true, host transition is disabled on all stacked cards (finger tracking). */
  readonly deckDragging = input(false);

  /** Fired after a left/right outcome: swipe pass, or like after the fly-out animation has started. */
  @Output() decision = new EventEmitter<{ profile: Profile; decision: SwipeDecision }>();
  @Output() superLike = new EventEmitter<Profile>();

  /** Top card only: updates parent `deckDx` / `deckDy` while dragging or animating fly-out. */
  readonly deckOffsetChange = output<{ dx: number; dy: number }>();
  /** Top card only: whether an active pointer drag is in progress (parent fans this out to every card). */
  readonly deckDragActiveChange = output<boolean>();

  /** True between pointerdown and pointerup while the user is dragging the top card. */
  private readonly dragging = signal(false);
  /** After a right-swipe fly-out starts, block further interaction until the parent removes this card. */
  private readonly disabled = signal(false);

  /** Index into `profile.photoUrls` for the hero image (tap left/right on photo to change). */
  private readonly photoIndex = signal(0);

  /** Pointer position at drag start (client coordinates). */
  private startX = 0;
  private startY = 0;
  /** Whether the current gesture began on the `.photo` area (enables tap-to-cycle vs swipe). */
  private startedInPhoto = false;
  /** Horizontal center of the photo at gesture start, used to decide previous vs next photo on tap. */
  private photoMidX = 0;

  /** 0–1 opacity for the green LIKE stamp while dragging right. */
  readonly likeStrength = computed(() => Math.max(0, Math.min(1, this.deckDx() / 160)));
  /** 0–1 opacity for the red NOPE stamp while dragging left. */
  readonly nopeStrength = computed(() => Math.max(0, Math.min(1, -this.deckDx() / 160)));

  /** CSS transform: drag translate + tilt only on the top card; others use stack offset only. */
  readonly stackTransform = computed(() => {
    const idx = this.stackIndex();
    const top = this.isTop();
    const scale = 1 - idx * 0.03;
    const translateY = idx * 10;
    const x = top ? this.deckDx() : 0;
    const y = top ? this.deckDy() : 0;
    const rotate = top ? this.deckDx() / 18 : 0;
    return `translate(${x}px, ${y + translateY}px) rotate(${rotate}deg) scale(${scale})`;
  });

  /** Resolved URL for the currently selected profile photo (empty if none). */
  readonly currentPhotoUrl = computed(() => {
    const urls = this.profile?.photoUrls ?? [];
    if (urls.length === 0) return "";
    const idx = ((this.photoIndex() % urls.length) + urls.length) % urls.length;
    return urls[idx] ?? "";
  });

  readonly photoCount = computed(() => (this.profile?.photoUrls ?? []).length);

  /** One boolean per dot: true where the dot matches the active photo index (for the carousel UI). */
  readonly photoDots = computed(() => {
    const n = this.photoCount();
    const idx = this.photoIndex();
    return Array.from({ length: n }, (_, i) => i === (((idx % n) + n) % n));
  });

  /** Host `transition`: off while any card in the deck is actively dragged; otherwise snap/fly-out animate. */
  readonly hostTransition = computed(() => {
    if (this.dragging() || this.deckDragging()) return "none";
    // Smoothly animate snap-back + fly-out.
    return "transform 220ms cubic-bezier(0.2, 0.9, 0.2, 1)";
  });

  constructor(private readonly el: ElementRef<HTMLElement>) {
    let previousTop = false;
    effect(() => {
      const top = this.isTop();
      if (top && !previousTop) {
        this.resetMotionState();
      }
      previousTop = top;
    });
  }

  /**
   * Resets drag offset, “interaction locked” flag, and drag flag so a recycled component instance
   * or a card promoted to the top does not keep another profile’s motion or a stuck `disabled` state.
   */
  private resetMotionState(): void {
    this.dragging.set(false);
    this.disabled.set(false);
    // Only the interactive top card may change shared deck offsets (avoid clearing while another card recycles).
    if (this.isTop()) {
      this.deckOffsetChange.emit({ dx: 0, dy: 0 });
      this.deckDragActiveChange.emit(false);
    }
  }

  private emitDeckOffset(dx: number, dy: number): void {
    if (!this.isTop()) return;
    this.deckOffsetChange.emit({ dx, dy });
  }

  private emitDeckDragActive(active: boolean): void {
    if (!this.isTop()) return;
    this.deckDragActiveChange.emit(active);
  }

  /**
   * Starts a drag on the top card only: captures the pointer, ignores starts on buttons/links,
   * records whether the gesture began on the photo (for tap-to-cycle), and wires window-level
   * move/up listeners so dragging still works if the pointer leaves the element.
   */
  onPointerDown(ev: PointerEvent): void {
    if (!this.isTop() || this.disabled()) return;
    // If the interaction started on a control inside the card (buttons/links),
    // don't initiate a swipe drag or capture the pointer.
    const target = ev.target as HTMLElement | null;
    if (target?.closest("button, a")) return;

    const photo = target?.closest(".photo") as HTMLElement | null;
    this.startedInPhoto = !!photo;
    if (photo) {
      const rect = photo.getBoundingClientRect();
      this.photoMidX = rect.left + rect.width / 2;
    }

    this.dragging.set(true);
    this.emitDeckDragActive(true);

    this.startX = ev.clientX;
    this.startY = ev.clientY;
    this.el.nativeElement.setPointerCapture(ev.pointerId);

    const onMove = (e: PointerEvent) => this.onPointerMove(e);
    const onUp = (e: PointerEvent) => this.onPointerUp(e, onMove, onUp);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true, once: true });
  }

  /** Updates `dx`/`dy` while dragging so the host transform follows the pointer. */
  private onPointerMove(ev: PointerEvent): void {
    if (!this.dragging()) return;
    this.emitDeckOffset(ev.clientX - this.startX, ev.clientY - this.startY);
  }

  /**
   * Ends the drag: removes listeners, releases capture, then either:
   * - treats a small movement on the photo as a tap (previous/next photo),
   * - flies out right if past the like threshold,
   * - emits left immediately with snap-back if past the nope threshold (parent shows pass dialogs),
   * - or snaps back to center if the swipe was too short.
   */
  private onPointerUp(ev: PointerEvent, onMove: (e: PointerEvent) => void, onUp: (e: PointerEvent) => void): void {
    const dx = ev.clientX - this.startX;
    const dy = ev.clientY - this.startY;

    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    this.dragging.set(false);
    this.emitDeckDragActive(false);
    try {
      this.el.nativeElement.releasePointerCapture(ev.pointerId);
    } catch {
      // ignore (capture may already be released)
    }

    // Tap-to-cycle on photo, but allow swiping from the photo too.
    const tapThreshold = 10;
    if (
      this.startedInPhoto &&
      this.photoCount() > 1 &&
      Math.abs(dx) <= tapThreshold &&
      Math.abs(dy) <= tapThreshold
    ) {
      const dir = ev.clientX < this.photoMidX ? -1 : 1;
      this.photoIndex.set(this.photoIndex() + dir);
      this.snapBack();
      return;
    }

    const threshold = 130;
    if (dx > threshold) {
      this.emitAndFlyOut("right", dy * 0.2);
      return;
    }
    if (dx < -threshold) {
      this.emitAndSnapBack("left");
      return;
    }

    this.snapBack();
  }

  /**
   * If capture is lost mid-drag (e.g. element removed or browser interruption), cancel the gesture
   * and snap the card back so it does not stay offset or leave `dragging` true (which would disable CSS transitions).
   */
  onLostPointerCapture(): void {
    if (!this.dragging()) return;
    this.dragging.set(false);
    this.emitDeckDragActive(false);
    this.snapBack();
  }

  /**
   * Used for a committed right swipe / Like: animates the card off-screen, then emits `decision` after a short delay
   * so the motion is visible before the parent opens the match dialog. `disabled` prevents double-submits.
   * Resets `dx`/`dy` to zero then applies the target on `requestAnimationFrame` so the host `transition` reliably runs.
   */
  private emitAndFlyOut(dir: SwipeDecision, carryYOverride?: number): void {
    this.disabled.set(true);
    this.dragging.set(false);
    const flyX = dir === "right" ? 900 : -900;
    const carryY = carryYOverride ?? this.deckDy() * 0.2;
    // Baseline then fly on the next frame so the transform transition always runs (avoids stale offset / coalesced updates).
    this.emitDeckOffset(0, 0);
    requestAnimationFrame(() => {
      this.emitDeckOffset(flyX, carryY);
      window.setTimeout(() => {
        this.decision.emit({ profile: this.profile, decision: dir });
      }, 140);
    });
  }

  /** Left / Nope path: notify parent immediately and reset position (parent handles confirmation UI). */
  private emitAndSnapBack(dir: SwipeDecision): void {
    this.decision.emit({ profile: this.profile, decision: dir });
    this.snapBack();
  }

  /** Returns the card to the neutral stack position (no horizontal/vertical drag offset). */
  private snapBack(): void {
    this.emitDeckOffset(0, 0);
  }

  /** Toolbar: trigger left/pass using the same pipeline as a left drag past threshold. */
  clickNope(): void {
    if (!this.isTop() || this.disabled()) return;
    this.emitAndSnapBack("left");
  }

  /** Toolbar: opens super-like flow in the parent (does not remove the card or emit `decision`). */
  clickSuperLike(): void {
    if (!this.isTop() || this.disabled()) return;
    this.superLike.emit(this.profile);
  }

  /** Toolbar: same as a successful right swipe — fly-out animation then `decision` with `"right"`. */
  clickLike(): void {
    if (!this.isTop() || this.disabled()) return;
    this.emitAndFlyOut("right");
  }
}

